import { useState, useRef } from "react";

// @mui
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import { useTheme } from "@mui/material/styles";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

import prevArrow from "./prev.svg";
import nextArrow from "./next.svg";
import close from "./close.svg";
import { Image } from "../../../../fe-ui/atoms/Image";
import { S3ImageLight, S3ImageLightSrc, useSize } from "../../../../fe-ui/atoms/S3Image/S3ImageLight";
import { fixAssetPath } from "../../../../fe-ui/utils/fixAssetPath";

type Props = {
  isLoading?: true;
  main?: {
    src: string;
    title?: string;
  };
  thumbs: {
    src: string;
    title?: string;
  }[];
  hasNoImgProxy?: boolean;
};

function NextArrow(props) {
  const { bgColor } = props;
  return (
    <img
      style={{
        width: 50,
        height: 50,
        padding: 15,
        right: 0,
        boxShadow: "-2px 8px 20px 2px rgba(145, 158, 171, 0.4)",
        borderRadius: "50px",
        backgroundColor: bgColor,
        zIndex: 1,
      }}
      src={fixAssetPath(nextArrow.src)}
      alt="Next arrow"
    />
  );
}

function PrevArrow(props) {
  const { bgColor } = props;
  return (
    <img
      style={{
        width: 50,
        height: 50,
        padding: 15,
        left: 0,
        boxShadow: "-2px 8px 20px 2px rgba(145, 158, 171, 0.4)",
        borderRadius: "50px",
        backgroundColor: bgColor,
        zIndex: 1,
      }}
      src={fixAssetPath(prevArrow.src)}
      alt="Previous arrow"
    />
  );
}

function Close(props) {
  const { bgColor } = props;
  return (
    <img
      style={{
        width: 50,
        height: 50,
        padding: 15,
        left: 0,
        boxShadow: "-2px 8px 20px 2px rgba(145, 158, 171, 0.4)",
        borderRadius: "50px",
        backgroundColor: bgColor,
        zIndex: 1,
      }}
      src={fixAssetPath(close.src)}
      alt="Close"
    />
  );
}

const PropertyPhoto = (props: Props) => {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("sm"));
  const { width: widthSmall, height: heightSmall } = useSize("small");
  const { width: widthMedium, height: heightMedium } = useSize("medium");

  const { main: _main, thumbs, hasNoImgProxy = false } = props;
  const thumbnailsRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(-1);

  const slides = hasNoImgProxy ? thumbs : thumbs.map((thumb) => ({ src: S3ImageLightSrc(thumb.src, "large") }));
  const otherThumbs = thumbs.slice(5).length;

  const css = `
  .yarl__thumbnails .yarl__thumbnails_vignette {
      display: none;
  }
  .yarl__thumbnails .yarl__button {
    filter: none;
  }
`;

  const main = _main || thumbs[0];

  if (!main) {
    return <>Empty</>;
  }

  if (isXS) {
    return (
      <div style={{ position: "relative" }}>
        <Swiper
          modules={[Pagination]}
          pagination={{
            el: ".swiper-custom-pagination",
          }}
        >
          {thumbs.map((thumb, index) => (
            <SwiperSlide key={`${thumb.src}-${index}`}>
              <Grid item xs={6}>
                <div
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 16,
                    marginLeft: 2,
                    marginRight: 2,
                  }}
                >
                  {hasNoImgProxy ? (
                    <Image
                      src={thumb.src}
                      alt={thumb?.title ?? ""}
                      width={widthSmall}
                      height={heightSmall}
                      ratio="1/1"
                    />
                  ) : (
                    <S3ImageLight src={thumb.src} alt={`${thumb.title}`} size="small" />
                  )}
                </div>
              </Grid>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="swiper-custom-pagination" style={{ marginTop: "20px", textAlign: "center" }} />
      </div>
    );
  }

  return (
    <>
      <style>{css}</style>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              cursor: "pointer",
              borderRadius: 2,
              height: "100%",
            }}
            onClick={() => {
              setOpen(true);
              setIndex(0);
            }}
          >
            {hasNoImgProxy ? (
              <Image src={main.src} alt={main?.title ?? ""} width={widthMedium} height={heightMedium} ratio="1/1" />
            ) : (
              <S3ImageLight sx={{ height: "100%" }} src={main.src} alt={`${main.title}`} size="medium" />
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ position: "relative" }}>
            {thumbs.slice(1, 5).map((thumb, i) => (
              <Grid item key={thumb.src} xs={6}>
                <Box
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    borderRadius: 2,
                  }}
                  onClick={() => {
                    setOpen(true);
                    setIndex(i + 1);
                  }}
                >
                  {hasNoImgProxy ? (
                    <Image
                      src={thumb.src}
                      alt={thumb?.title ?? ""}
                      width={widthSmall}
                      height={heightSmall}
                      ratio="1/1"
                    />
                  ) : (
                    <S3ImageLight sx={{ height: "100%" }} src={thumb.src} alt={`${thumb.title}`} size="small" />
                  )}
                </Box>
              </Grid>
            ))}
            {otherThumbs > 0 ? (
              <Typography
                variant={"h4"}
                sx={{ position: "absolute", right: 10, bottom: 0, color: theme.palette.background.default }}
              >
                {"+"}
                {otherThumbs} <ImageSearchIcon />
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      </Grid>

      <Lightbox
        plugins={[Thumbnails]}
        thumbnails={{ ref: thumbnailsRef }}
        styles={{
          container: { backgroundColor: "rgba(255, 255, 255, .95)" },
          thumbnailsContainer: { backgroundColor: "rgba(255, 255, 255, .95)" },
          thumbnail: { backgroundColor: "rgba(255, 255, 255, .95)" },
        }}
        render={{
          iconPrev: () => <PrevArrow isXS={isXS} bgColor={theme.palette.background.default} />,
          iconNext: () => <NextArrow isXS={isXS} bgColor={theme.palette.background.default} />,
          iconClose: () => <Close isXS={isXS} bgColor={theme.palette.background.default} />,
        }}
        index={index}
        open={open}
        close={() => setOpen(false)}
        slides={slides}
      />
    </>
  );
};

export { PropertyPhoto };
